import React, { useEffect, useRef, useState } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { tokens, CHART_PALETTE } from '../../theme';
import { formatNumber } from '../../utils/format';

// Lekkie wykresy bez zaleznosci: kolory z CHART_PALETTE, osie i etykiety w tokens.muted (min. 12 px),
// linie siatki w tokens.line. Slupki i etykiety sa w HTML, zeby tekst nie skalowal sie razem z SVG.

interface Series {
  label: string;
  /** Kolor serii; domyslnie kolejny kolor z CHART_PALETTE. */
  color?: string;
  values: number[];
}

const AXIS_LABEL_SX = {
  fontSize: '0.75rem',
  lineHeight: '16px',
  fontWeight: 500,
  color: tokens.muted,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
} as const;

const seriesColor = (s: Series, i: number) => s.color || CHART_PALETTE[i % CHART_PALETTE.length];

/** Czytelna podzialka osi: 0, 250, 500… zamiast 0, 237, 474… */
const niceScale = (rawMin: number, rawMax: number, count = 4) => {
  const min = Math.min(0, rawMin);
  const max = rawMax <= min ? min + 1 : rawMax;
  const raw = (max - min) / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag;
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = hi; v >= lo - step / 2; v -= step) ticks.push(Number(v.toFixed(6)));
  return { lo, hi, ticks };
};

/** Szerokosc kontenera — do przerzedzania etykiet osi X na waskich ekranach. */
const useWidth = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => setWidth(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
};

const labelStep = (categories: string[], width: number) => {
  if (!width || categories.length === 0) return 1;
  const longest = Math.max(...categories.map((c) => c.length));
  const perLabel = Math.max(28, longest * 7 + 10);
  return Math.max(1, Math.ceil(categories.length / Math.max(1, Math.floor(width / perLabel))));
};

const Legend: React.FC<{ series: Series[]; line?: boolean }> = ({ series, line }) => (
  <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mb: 1.5 }}>
    {series.map((s, i) => (
      <Stack key={s.label} direction="row" alignItems="center" gap={0.75}>
        <Box sx={{ width: 12, height: line ? 3 : 12, borderRadius: line ? '2px' : '3px', bgcolor: seriesColor(s, i) }} />
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: tokens.inkSoft }}>{s.label}</Typography>
      </Stack>
    ))}
  </Stack>
);

/** Kolumna z wartosciami osi Y wyrownanymi do linii siatki. */
const YAxis: React.FC<{ ticks: number[]; plotHeight: number; format: (v: number) => string }> = ({ ticks, plotHeight, format }) => (
  <Stack justifyContent="space-between" alignItems="flex-end" sx={{ height: plotHeight + 16, my: '-8px', pr: 1 }} aria-hidden>
    {ticks.map((t) => (
      <Typography key={t} component="span" sx={AXIS_LABEL_SX}>{format(t)}</Typography>
    ))}
  </Stack>
);

const GridLines: React.FC<{ ticks: number[]; lo: number; hi: number }> = ({ ticks, lo, hi }) => (
  <>
    {ticks.map((t) => (
      <Box
        key={t}
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: `${((hi - t) / (hi - lo || 1)) * 100}%`,
          borderTop: `1px solid ${t === 0 && lo < 0 ? tokens.lineStrong : tokens.line}`,
        }}
      />
    ))}
  </>
);

export const GroupedBarChart: React.FC<{
  categories: string[];
  series: Series[];
  height?: number;
  formatValue?: (v: number) => string;
}> = ({ categories, series, height = 200, formatValue }) => {
  const [ref, width] = useWidth();
  const plotHeight = Math.max(80, height - 24);
  const { lo, hi, ticks } = niceScale(0, Math.max(0, ...series.flatMap((s) => s.values)));
  const fmt = formatValue || ((v: number) => formatNumber(v));
  const step = labelStep(categories, width);

  return (
    <Box>
      <Legend series={series} />
      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)' }}>
        <YAxis ticks={ticks} plotHeight={plotHeight} format={fmt} />
        <Box ref={ref} sx={{ position: 'relative', height: plotHeight, minWidth: 0 }}>
          <GridLines ticks={ticks} lo={lo} hi={hi} />
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex' }}>
            {categories.map((cat, ci) => (
              <Stack key={`${cat}-${ci}`} direction="row" alignItems="flex-end" justifyContent="center" sx={{ flex: '1 1 0', minWidth: 0, px: '12%', gap: '6%' }}>
                {series.map((s, si) => {
                  const v = s.values[ci] ?? 0;
                  return (
                    <Tooltip key={s.label} title={`${cat} · ${s.label}: ${fmt(v)}`} placement="top">
                      <Box
                        sx={{
                          flex: '1 1 0',
                          maxWidth: 36,
                          minWidth: 2,
                          height: `${(Math.max(0, v) / (hi || 1)) * 100}%`,
                          bgcolor: seriesColor(s, si),
                          borderRadius: '4px 4px 0 0',
                          transition: 'opacity .15s ease',
                          '&:hover': { opacity: 0.85 },
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Stack>
            ))}
          </Box>
        </Box>
        <Box />
        <Box sx={{ display: 'flex', height: 24, minWidth: 0 }} aria-hidden>
          {categories.map((cat, ci) => (
            <Box key={`${cat}-${ci}`} sx={{ position: 'relative', flex: '1 1 0', minWidth: 0 }}>
              {ci % step === 0 && (
                <Typography component="span" sx={{ ...AXIS_LABEL_SX, position: 'absolute', top: 6, left: '50%', transform: 'translateX(-50%)' }}>
                  {cat}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export const LineChart: React.FC<{
  categories: string[];
  series: Series[];
  height?: number;
  formatValue?: (v: number) => string;
}> = ({ categories, series, height = 200, formatValue }) => {
  const [ref, width] = useWidth();
  const plotHeight = Math.max(80, height - 24);
  const all = series.flatMap((s) => s.values);
  const { lo, hi, ticks } = niceScale(Math.min(0, ...all), Math.max(0, ...all));
  const fmt = formatValue || ((v: number) => formatNumber(v));
  const n = categories.length;
  const xPct = (i: number) => (n <= 1 ? 50 : (i / (n - 1)) * 100);
  const yPct = (v: number) => ((hi - v) / (hi - lo || 1)) * 100;
  const step = labelStep(categories, width);

  return (
    <Box>
      <Legend series={series} line />
      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)' }}>
        <YAxis ticks={ticks} plotHeight={plotHeight} format={fmt} />
        <Box sx={{ position: 'relative', height: plotHeight, minWidth: 0 }}>
          <GridLines ticks={ticks} lo={lo} hi={hi} />
          {/* Wciecie po bokach, zeby pierwsza i ostatnia etykieta nie wychodzily poza wykres */}
          <Box ref={ref} sx={{ position: 'absolute', top: 0, bottom: 0, left: 16, right: 16 }}>
            <svg width="100%" height="100%" preserveAspectRatio="none" viewBox="0 0 100 100" style={{ position: 'absolute', inset: 0, overflow: 'visible' }}>
              {series.map((s, si) => (
                <polyline
                  key={s.label}
                  points={s.values.map((v, i) => `${xPct(i)},${yPct(v)}`).join(' ')}
                  fill="none"
                  stroke={seriesColor(s, si)}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
            </svg>
            {series.map((s, si) => s.values.map((v, i) => (
              <Tooltip key={`${s.label}-${i}`} title={`${categories[i] ?? ''} · ${s.label}: ${fmt(v)}`} placement="top">
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${xPct(i)}%`,
                    top: `${yPct(v)}%`,
                    width: 8,
                    height: 8,
                    ml: '-4px',
                    mt: '-4px',
                    borderRadius: '50%',
                    bgcolor: tokens.paper,
                    border: `2px solid ${seriesColor(s, si)}`,
                    '&:hover': { transform: 'scale(1.4)' },
                  }}
                />
              </Tooltip>
            )))}
          </Box>
        </Box>
        <Box />
        <Box sx={{ position: 'relative', height: 24, mx: 2 }} aria-hidden>
          {categories.map((c, i) => (i % step === 0 ? (
            <Typography
              key={`${c}-${i}`}
              component="span"
              sx={{ ...AXIS_LABEL_SX, position: 'absolute', top: 6, left: `${xPct(i)}%`, transform: 'translateX(-50%)' }}
            >
              {c}
            </Typography>
          ) : null))}
        </Box>
      </Box>
    </Box>
  );
};
